<template>
  <div class="select-wrapper" :class="wrapperClasses">
    <select
      ref="selectRef"
      v-bind="$attrs"
      :class="selectClass"
      :value="modelValue"
      :disabled="disabled"
      :aria-invalid="hasError"
      :aria-describedby="errorMessages.length > 0 ? `${selectId}-errors` : undefined"
      @change="onChange"
      @focus="onFocus"
      @blur="onBlur"
    >
      <option v-if="placeholder" value="" disabled>
        {{ placeholder }}
      </option>
      <option
        v-for="option in options"
        :key="getOptionValue(option)"
        :value="getOptionValue(option)"
        :disabled="typeof option === 'object' ? option.disabled : false"
      >
        {{ getOptionLabel(option) }}
      </option>
    </select>
  <Icon name="caret-down" class="select-icon" :size="12" />
    <div v-if="showValidationIcon" class="validation-icon">
      <Icon
        :name="hasError ? 'warning-circle' : 'check'"
        :size="14"
        :class="hasError ? 'validation-icon--error' : 'validation-icon--success'"
      />
    </div>
  </div>
  
  <div
    v-if="errorMessages.length > 0"
    :id="`${selectId}-errors`"
    class="error-messages"
    role="alert"
    aria-live="polite"
  >
    <div
      v-for="(error, index) in errorMessages"
      :key="index"
      class="error-message"
    >
  <Icon name="warning-circle" :size="12" class="error-message-icon" />
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Icon from './Icon.vue';

// Disable automatic attribute inheritance since we manually handle it
defineOptions({
  inheritAttrs: false
});

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  description?: string;
}

export interface SelectBoxProps {
  modelValue: string | number;
  options: SelectOption[] | string[];
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  // Validation props
  errorMessages?: string[];
  hasError?: boolean;
  showValidationIcon?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  selectId?: string;
}

const props = withDefaults(defineProps<SelectBoxProps>(), {
  disabled: false,
  size: 'medium',
  errorMessages: () => [],
  hasError: false,
  showValidationIcon: false,
  validateOnBlur: true,
  validateOnChange: true,
  selectId: () => `select-${Math.random().toString(36).substr(2, 9)}`
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  change: [value: string | number];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
  validate: [value: string | number];
}>();

const selectRef = ref<HTMLSelectElement>();

const selectClass = computed(() => [
  'select',
  `select--${props.size}`,
  {
    'select--disabled': props.disabled,
    'select--error': props.hasError && props.errorMessages.length > 0,
    'select--success': props.showValidationIcon && !props.hasError && props.modelValue
  }
]);

const wrapperClasses = computed(() => ({
  'select-wrapper--error': props.hasError && props.errorMessages.length > 0,
  'select-wrapper--success': props.showValidationIcon && !props.hasError && props.modelValue,
  'select-wrapper--with-validation': props.showValidationIcon
}));

const getOptionValue = (option: SelectOption | string): string | number => {
  return typeof option === 'string' ? option : option.value;
};

const getOptionLabel = (option: SelectOption | string): string => {
  return typeof option === 'string' ? option : option.label;
};

const onChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  const value = target.value;
  
  // Try to convert to number if the original option value was a number
  const numValue = Number(value);
  const finalValue = !isNaN(numValue) && value !== '' ? numValue : value;
  
  emit('update:modelValue', finalValue);
  emit('change', finalValue);
  
  if (props.validateOnChange) {
    emit('validate', finalValue);
  }
};

const onFocus = (event: FocusEvent) => {
  emit('focus', event);
};

const onBlur = (event: FocusEvent) => {
  emit('blur', event);
  
  if (props.validateOnBlur) {
    emit('validate', props.modelValue);
  }
};

defineExpose({
  focus: () => selectRef.value?.focus(),
  blur: () => selectRef.value?.blur(),
});
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.select-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.select {
  @include select-style;
  width: 100%;
  appearance: none;
  cursor: pointer;
  padding-right: var(--spacing-xxl);
  outline: none;
  transition: border-color 0.1s ease;
  
  &--small {
    padding: var(--spacing-xs) var(--spacing-xxl) var(--spacing-xs) var(--spacing-sm);
    font-size: 12px;
  }
  
  &--medium {
    padding: var(--spacing-sm) var(--spacing-xxl) var(--spacing-sm) var(--spacing-md);
    font-size: var(--vscode-font-size);
  }
  
  &--large {
    padding: var(--spacing-md) var(--spacing-xxl) var(--spacing-md) var(--spacing-lg);
    font-size: 14px;
  }
  
  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &--error {
    border-color: var(--vscode-inputValidation-errorBorder);
    background-color: var(--vscode-inputValidation-errorBackground);
    
    &:focus {
      border-color: var(--vscode-inputValidation-errorBorder);
    }
  }
  
  &--success {
    border-color: var(--vscode-terminal-ansiGreen);
    
    &:focus {
      border-color: var(--vscode-terminal-ansiGreen);
    }
  }
  
  &:focus {
    @include focus-outline;
    border-color: var(--vscode-focusBorder);
  }
  
  &:hover:not(.select--disabled) {
    border-color: var(--vscode-focusBorder);
  }
}

.select-icon {
  position: absolute;
  right: var(--spacing-md);
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--vscode-foreground);
  opacity: 0.7;
  
  .select--disabled + & {
    opacity: 0.3;
  }
}

// Custom option styling (limited browser support)
option {
  background-color: var(--vscode-settings-selectBackground);
  color: var(--vscode-settings-selectForeground);
  
  &:disabled {
    color: var(--vscode-foreground);
    opacity: 0.5;
  }
}

// Validation styles
.validation-icon {
  position: absolute;
  right: var(--spacing-xl);
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.validation-icon--error {
  color: var(--vscode-inputValidation-errorForeground);
}

.validation-icon--success {
  color: var(--vscode-terminal-ansiGreen);
}

.select-wrapper--with-validation .select {
  padding-right: calc(var(--spacing-xxl) * 2.5);
}

.error-messages {
  margin-top: var(--spacing-xs);
}

.error-message {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  font-size: 12px;
  color: var(--vscode-inputValidation-errorForeground);
  background-color: var(--vscode-inputValidation-errorBackground);
  border: 1px solid var(--vscode-inputValidation-errorBorder);
  border-radius: var(--border-radius);
  padding: var(--spacing-xs) var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
  
  &:last-child {
    margin-bottom: 0;
  }
}

.error-message-icon {
  color: var(--vscode-inputValidation-errorForeground);
  flex-shrink: 0;
  margin-top: 1px; // Align with text baseline
}
</style>
